import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { IPresignFileResponse, PresignFileResponse } from '../models/presign-file.model';
import { IServerResponse } from '../models/server-response.model';
import { ITemplateField, TemplateFieldModel } from '../models/template-field.model';
import { ITemplate, TemplateModel } from '../models/template.model';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { UtilService } from './util.service';

/** Body de `POST /templates`. */
export interface ITemplateCreateRequest {
  code: string;
  name: string;
  description: string;
  generateQr: boolean;
  /** MIME del archivo a subir. `null` = no se sube nada en esta llamada. */
  contentType: string | null;
}

/** Body de `PUT /templates/{templateId}`. OJO: `code` NO se actualiza. */
export interface ITemplateUpdateRequest {
  name: string;
  description: string;
  generateQr: boolean;
  status: boolean;
  contentType: string | null;
}

/** Body de `POST /templates/{templateId}/fields`. */
export interface ITemplateFieldCreateRequest {
  fieldType: string;
  label: string;
  position: number;
  required: boolean;
  /**
   * Marca este campo como el campo principal de la plantilla. El API apaga
   * en la misma transaccion el `primaryField` de cualquier otro campo de la
   * plantilla: NUNCA hay que mandar un PUT extra por los hermanos. Responde
   * 400 si el `fieldType` elegido es `protected`.
   */
  primaryField: boolean;
}

/** Body de `PUT /templates/{templateId}/fields/{templateFieldId}`. */
export interface ITemplateFieldUpdateRequest {
  label: string;
  position: number;
  required: boolean;
  /** Mismo comportamiento atomico que en el alta. Ver `ITemplateFieldCreateRequest`. */
  primaryField: boolean;
  status: boolean;
}

/**
 * Resultado de crear / actualizar una plantilla: el registro y, cuando se
 * mando `contentType`, la URL firmada para subir el archivo a S3.
 */
export interface ITemplateMutationResult {
  template: TemplateModel;
  presignFile: PresignFileResponse | null;
}

/**
 * Catalogo de plantillas y de sus campos.
 *
 * Los campos NO tienen GET propio: llegan dentro de `template.fields`.
 * El listado no tiene paginacion y viene ordenado por `name asc`.
 */
@Injectable({ providedIn: 'root' })
export class TemplateService {
  private readonly http = inject(HttpClient);
  private readonly spinner = inject(SpinnerService);
  private readonly toast = inject(ToastService);
  private readonly util = inject(UtilService);
  private readonly urlApi = `${environment.urlApi}/templates`;

  readonly loading = signal(false);
  readonly templates = signal<TemplateModel[]>([]);
  readonly template = signal<TemplateModel | null>(null);

  /**
   * `false` mientras NO se sabe todavia si hay datos: la primera peticion no ha
   * terminado. La vista pinta esqueletos hasta que pase a `true`; sin esta
   * senal el primer render enseñaria el estado vacio con la lista aun cargando.
   */
  readonly templatesLoaded = signal(false);

  /** Mensaje del ultimo fallo de carga del listado, o `null` si salio bien. */
  readonly templatesError = signal<string | null>(null);

  /**
   * Mensaje del ultimo fallo de carga del DETALLE, o `null` si salio bien.
   *
   * El detalle NO expone aqui su bandera de «ya cargo»: el servicio es un
   * singleton y esa bandera seguiria encendida al abrir OTRA plantilla, asi que
   * ese estado vive en el componente, donde muere con cada instancia.
   */
  readonly templateError = signal<string | null>(null);

  async getTemplates(): Promise<TemplateModel[]> {
    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ templates: ITemplate[] }>>(this.urlApi),
      );

      if (response.statusCode !== 200) {
        this.templatesError.set(response.message);
        this.toast.error(response.message);
        return [];
      }

      const templates = (response.data?.templates ?? []).map((item) => new TemplateModel(item));
      this.templates.set(templates);
      this.templatesError.set(null);
      return templates;
    } catch (error) {
      const message = this.util.errorMessage(error, 'No se pudieron cargar las plantillas.');
      this.templatesError.set(message);
      this.toast.error(message);
      return [];
    } finally {
      this.loading.set(false);
      this.templatesLoaded.set(true);
    }
  }

  async getTemplate(templateId: string): Promise<TemplateModel | null> {
    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ template: ITemplate }>>(`${this.urlApi}/${templateId}`),
      );

      if (response.statusCode !== 200) {
        this.templateError.set(response.message);
        this.toast.error(response.message);
        this.template.set(null);
        return null;
      }

      const template = response.data?.template ? new TemplateModel(response.data.template) : null;
      this.template.set(template);
      this.templateError.set(null);
      return template;
    } catch (error) {
      const message = this.util.errorMessage(error, 'No se pudo cargar la plantilla.');
      this.templateError.set(message);
      this.toast.error(message);
      this.template.set(null);
      return null;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * POST devuelve 201. Un 409 significa que el `code` ya esta tomado.
   *
   * @param showSpinner Igual criterio que `updateTemplate` (ver su doc):
   * `templates-page` pasa `false` porque el alta ahora es OPTIMISTA (la fila
   * "Guardando…" ya aparece en la tabla antes de este POST); el overlay
   * bloqueante global lo hubiera dejado igual de congelado que el `await`
   * que se elimino en el componente. Por defecto `true` para no cambiar a
   * ningun otro llamante que no pase este parametro.
   */
  async createTemplate(
    request: ITemplateCreateRequest,
    showSpinner = true,
  ): Promise<ITemplateMutationResult | null> {
    if (showSpinner) this.spinner.show('Creando plantilla...');
    try {
      const response = await firstValueFrom(
        this.http.post<IServerResponse<{ template: ITemplate; presignFile?: IPresignFileResponse }>>(
          this.urlApi,
          request,
        ),
      );

      if (response.statusCode !== 201 || !response.data?.template) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return {
        template: new TemplateModel(response.data.template),
        presignFile: response.data.presignFile
          ? new PresignFileResponse(response.data.presignFile)
          : null,
      };
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo crear la plantilla.'));
      return null;
    } finally {
      if (showSpinner) this.spinner.hide();
    }
  }

  /**
   * @param showSpinner Antes lo decidia siempre este servicio. Ahora NINGUN
   * llamante quiere el overlay bloqueante para la plantilla en si: tanto
   * `templates-page` (el listado) como `template-page` (la ficha) usan el
   * patron OPTIMISTA (fila "Guardando…" al instante, PUT en segundo plano)
   * porque el dueño pidio poder seguir operando la pantalla mientras se
   * guarda. En vez de que el servicio adivine quien lo llama, la decision
   * pasa a quien invoca el metodo. Por defecto `true` por si algun llamante
   * futuro no pasa este parametro.
   */
  async updateTemplate(
    templateId: string,
    request: ITemplateUpdateRequest,
    showSpinner = true,
  ): Promise<ITemplateMutationResult | null> {
    if (showSpinner) this.spinner.show('Guardando plantilla...');
    try {
      const response = await firstValueFrom(
        this.http.put<IServerResponse<{ template: ITemplate; presignFile?: IPresignFileResponse }>>(
          `${this.urlApi}/${templateId}`,
          request,
        ),
      );

      if (response.statusCode !== 200 || !response.data?.template) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return {
        template: new TemplateModel(response.data.template),
        presignFile: response.data.presignFile
          ? new PresignFileResponse(response.data.presignFile)
          : null,
      };
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo guardar la plantilla.'));
      return null;
    } finally {
      if (showSpinner) this.spinner.hide();
    }
  }

  /**
   * DELETE es soft-delete: deja `status = false`.
   *
   * @param showSpinner Mismo criterio que `createTemplate`/`updateTemplate`:
   * `templates-page` pasa `false` porque desactivar tambien es OPTIMISTA
   * ahora (la fila pasa a "Desactivando…" al instante, el DELETE corre en
   * segundo plano). El dueño fue explicito: NINGUNA mutacion de esta
   * pantalla puede bloquearla, sin excepciones. Por defecto `true` para no
   * cambiar a ningun otro llamante que no pase este parametro.
   */
  async deleteTemplate(templateId: string, showSpinner = true): Promise<TemplateModel | null> {
    if (showSpinner) this.spinner.show('Desactivando plantilla...');
    try {
      const response = await firstValueFrom(
        this.http.delete<IServerResponse<{ template: ITemplate }>>(`${this.urlApi}/${templateId}`),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.template ? new TemplateModel(response.data.template) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo desactivar la plantilla.'));
      return null;
    } finally {
      if (showSpinner) this.spinner.hide();
    }
  }

  /**
   * POST devuelve 201. Un 400 significa tipo de campo inexistente o inactivo.
   *
   * SIN spinner bloqueante: uso exclusivo de `template-page`, que lo pilota
   * con un patron OPTIMISTA (la fila ya aparece en la tabla, marcada como
   * "guardando", antes de que este POST resuelva). Verificado por grep que
   * ningun otro llamante depende del overlay global aqui.
   */
  async createTemplateField(
    templateId: string,
    request: ITemplateFieldCreateRequest,
  ): Promise<TemplateFieldModel | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<IServerResponse<{ templateField: ITemplateField }>>(
          `${this.urlApi}/${templateId}/fields`,
          request,
        ),
      );

      if (response.statusCode !== 201) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.templateField
        ? new TemplateFieldModel(response.data.templateField)
        : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo crear el campo.'));
      return null;
    }
  }

  /**
   * SIN spinner bloqueante, por el mismo motivo que `createTemplateField`.
   *
   * @param silent Antes apagaba el toast Y el spinner; ahora que el spinner ya
   * no vive aqui solo apaga el toast. Lo sigue usando el reordenamiento
   * (`saveOrder`), que dispara un PUT por campo movido, envuelve el lote con
   * su propio `spinnerService.show()` y anuncia el resultado una sola vez al
   * terminar en vez de un toast por campo.
   */
  async updateTemplateField(
    templateId: string,
    templateFieldId: string,
    request: ITemplateFieldUpdateRequest,
    silent = false,
  ): Promise<TemplateFieldModel | null> {
    try {
      const response = await firstValueFrom(
        this.http.put<IServerResponse<{ templateField: ITemplateField }>>(
          `${this.urlApi}/${templateId}/fields/${templateFieldId}`,
          request,
        ),
      );

      if (response.statusCode !== 200) {
        if (!silent) this.toast.error(response.message);
        return null;
      }

      if (!silent) this.toast.success(response.message);
      return response.data?.templateField
        ? new TemplateFieldModel(response.data.templateField)
        : null;
    } catch (error) {
      if (!silent) this.toast.error(this.util.errorMessage(error, 'No se pudo guardar el campo.'));
      return null;
    }
  }

  /**
   * DELETE es soft-delete: deja `status = false`. SIN spinner bloqueante: la
   * fila se deshabilita en la propia tabla mientras se resuelve, sin overlay
   * global, y NO se quita de la lista hasta que el servidor confirma.
   */
  async deleteTemplateField(
    templateId: string,
    templateFieldId: string,
  ): Promise<TemplateFieldModel | null> {
    try {
      const response = await firstValueFrom(
        this.http.delete<IServerResponse<{ templateField: ITemplateField }>>(
          `${this.urlApi}/${templateId}/fields/${templateFieldId}`,
        ),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.templateField
        ? new TemplateFieldModel(response.data.templateField)
        : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo desactivar el campo.'));
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Reconciliacion LOCAL del patron optimista de `template-page`
  // ---------------------------------------------------------------------------
  // Estas tres funciones NO llaman al API: ajustan en el momento la copia de
  // `template().fields` que expone este servicio para que avance SIEMPRE junto
  // con la lista de trabajo que pinta `template-page` (`orderedFields`). Sin
  // esto, `template()` se quedaria desfasado de lo que la ficha ya mostro en
  // pantalla y `orderChanged()` —que compara contra `template().fields`—
  // marcaria un "orden sin guardar" que no existe (o al reves, lo ocultaria
  // cuando si lo hay). El POST/PUT/DELETE real lo hacen los metodos de arriba;
  // estas solo reflejan su resultado (o el estado optimista, antes de que el
  // servidor responda) en el signal compartido.
  //
  // Clonan el `TemplateModel` en vez de mutarlo en el sitio: un signal solo
  // notifica a quien lo lee cuando el valor cambia de REFERENCIA (usa
  // `Object.is` por defecto), asi que reasignar `template.fields` y volver a
  // `.set()` la MISMA instancia no dispararia ningun `computed` ni `effect`.

  private cloneTemplateWithFields(template: TemplateModel, fields: TemplateFieldModel[]): TemplateModel {
    return Object.assign(Object.create(Object.getPrototypeOf(template) as object), template, {
      fields,
    }) as TemplateModel;
  }

  /** Inserta un campo (optimista o ya confirmado) al final de la lista. */
  addFieldLocally(field: TemplateFieldModel): void {
    const template = this.template();
    if (!template) return;
    this.template.set(this.cloneTemplateWithFields(template, [...template.fields, field]));
  }

  /** Sustituye el campo con `templateFieldId` por otra version, en su mismo lugar. */
  replaceFieldLocally(templateFieldId: string, field: TemplateFieldModel): void {
    const template = this.template();
    if (!template) return;
    const fields = template.fields.map((item) =>
      item.templateFieldId === templateFieldId ? field : item,
    );
    this.template.set(this.cloneTemplateWithFields(template, fields));
  }

  /** Quita un campo de la lista (alta optimista descartada tras un fallo). */
  removeFieldLocally(templateFieldId: string): void {
    const template = this.template();
    if (!template) return;
    const fields = template.fields.filter((item) => item.templateFieldId !== templateFieldId);
    this.template.set(this.cloneTemplateWithFields(template, fields));
  }

  /**
   * Sube el archivo a la URL firmada de S3.
   *
   * CRITICO: a S3 NO puede viajar el header `Authorization`. `authInterceptor`
   * hace early-return para toda URL que no empiece por `environment.urlApi`,
   * asi que este PUT sale limpio con el HttpClient normal. El `Content-Type`
   * debe ser EXACTAMENTE el `contentType` que se mando al API, o la firma no
   * cuadra. La URL caduca a los 300 s.
   *
   * @param showSpinner Mismo criterio que `createTemplate`/`updateTemplate`:
   * `templates-page` (el listado) pasa `false` porque la subida corre DENTRO
   * del flujo optimista de `create()`/`edit()` — la fila ya esta marcada
   * "Guardando…"/"Guardando cambios…" y debe seguir asi hasta que la imagen
   * termine, sin overlay bloqueante encima. `template-page` (la ficha de
   * detalle) no pasa este parametro y se queda con el overlay de siempre.
   * Por defecto `true` para no cambiar a ningun otro llamante.
   */
  async uploadFile(
    presignFile: PresignFileResponse,
    file: File,
    showSpinner = true,
  ): Promise<boolean> {
    if (showSpinner) this.spinner.show('Subiendo imagen...');
    try {
      await firstValueFrom(
        this.http.put(presignFile.uploadUrl, file, {
          headers: new HttpHeaders({ 'Content-Type': file.type }),
          responseType: 'text',
        }),
      );
      return true;
    } catch (error) {
      this.toast.error(
        this.util.errorMessage(error, 'No se pudo subir la imagen. Intenta de nuevo.'),
      );
      return false;
    } finally {
      if (showSpinner) this.spinner.hide();
    }
  }
}
